import React from 'react'
import { FormProvider } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { ModalBase, IModalBaseProps, MODAL_TYPES } from 'transfix-ui/components/Modals'
import { makeStyles } from 'transfix-ui/core'
import { ICarrierState } from 'types'
import { IStore } from 'apps/carrier/utils/reducers'
import { InlineMessage, INLINE_MESSAGE_VARIANTS, useGlobalToast } from 'transfix-ui'
import { useCarrierStateQuery } from '../graphql/CarrierState'
import { useCarrierStatusFlagMutation } from '../graphql/CarrierStatusFlagMutation'
import { useMotorCarrierComplianceFlagsQuery } from '../graphql/MotorCarrierComplianceFlags'
import { CarrierAddFlagModalForm } from './CarrierAddFlagModalForm'
import { TCarrierAddFlagModalSchema } from './CarrierAddFlagModalSchema'
import { useCarrierAddFlagModalForm } from './useCarrierAddFlagModalForm'

interface ICarrierAddFlagModalProps extends Omit<IModalBaseProps, 'modalType'> {}

const useStyles = makeStyles({
  body: {
    '& .MuiFormControlLabel-root': {
      marginBottom: '24px',
    },
  },
})

export const CarrierAddFlagModal = ({
  onClose,
  open = false,
  ...props
}: ICarrierAddFlagModalProps) => {
  const { openToast } = useGlobalToast()
  const methods = useCarrierAddFlagModalForm({
    defaultValues: {},
  })

  const { handleSubmit } = methods || {}
  const { carrierDetails } = useSelector<IStore, ICarrierState>(({ carrier }) => carrier)
  const { id: carrierApplicationId = '' } = carrierDetails || {}
  const classes = useStyles()

  const [carrierStatusFlagMutation, { error, loading }] = useCarrierStatusFlagMutation()
  const { data, refetch: flagsRefetch } = useMotorCarrierComplianceFlagsQuery({
    variables: {
      carrierApplicationId,
    },
  })
  const { refetch: stateRefetch } =
    useCarrierStateQuery({ variables: { carrierApplicationId } }) || {}

  const submitHandler = async (data: TCarrierAddFlagModalSchema) => {
    const { flagName: flag } = data

    const flagName = flag === 'removeFlag' ? null : flag
    const { data: response, errors } = await carrierStatusFlagMutation({
      variables: {
        input: {
          carrierApplicationId,
          flagName,
        },
      },
    })

    const { createMotorCarrierComplianceFlag } = response || {}
    const { errors: responseErrors } = createMotorCarrierComplianceFlag || {}
    const allErrors = [...(errors || []), ...(responseErrors || [])]

    if (allErrors.length) {
      return openToast({
        toastMessage:
          allErrors[0].message
            .replace(/([a-z]+_[a-z]+)+/g, '')
            .replace(':', '')
            .trim() || 'Something went wrong',
        toastType: 'alert',
      })
    }

    stateRefetch()
    flagsRefetch()
    return onClose()
  }

  const actions: IModalBaseProps['actions'] = [
    {
      label: 'Cancel',
      action: () => {
        onClose()
      },
      type: 'dismiss',
      dataTest: 'cancel-button',
    },
    {
      label: 'Save',
      disabled: loading,
      action: handleSubmit(submitHandler),
      type: 'confirm',
      dataTest: 'confirm-button',
    },
  ]

  return (
    <ModalBase
      dataTest='CarrierAddFlagModal'
      modalType={MODAL_TYPES.form}
      size={6}
      open={open}
      actions={actions}
      bodyClassName={classes.body}
      onClose={onClose}
      title='Add Flag'
      {...props}>
      <FormProvider {...methods}>
        <CarrierAddFlagModalForm flagData={data} />
      </FormProvider>
      {error && (
        <InlineMessage
          type={INLINE_MESSAGE_VARIANTS.WARNING}
          message={error?.message || 'Something went wrong'}
        />
      )}
    </ModalBase>
  )
}
